import React from 'react';
import { Helmet } from 'react-helmet-async';
import { Link } from 'react-router-dom';
import AnimateOnScroll from './AnimateOnScroll.js';
import PageHero from './PageHero.js';
import heroUtility from '../assets/hero-utility.jpg';

const UtilityPage = () => {
  return (
    <div className="bg-custom-earth">
      <div className="wood-container">
        <div className="min-h-screen flex flex-col">
          <Helmet>
            <title>Utility Corridor Vegetation Management BC | Boultbee</title>
            <meta name="description" content="Vegetation management for BC utility corridors, substations, and transmission lines. Power line clearance and cable strip maintenance for power, gas, and telecom providers." />
            <link rel="canonical" href="https://www.weedmanager.com/industries/utility" />
            <meta property="og:type" content="website" />
            <meta property="og:site_name" content="Boultbee Vegetation Management" />
            <meta property="og:title" content="Utility Corridor Vegetation Management BC | Boultbee" />
            <meta property="og:description" content="Vegetation management for BC utility corridors, substations, and transmission lines. Power line clearance and cable strip maintenance for power, gas, and telecom providers." />
            <meta property="og:url" content="https://www.weedmanager.com/industries/utility" />
            <meta property="og:image" content="https://www.weedmanager.com/images/og-utility.jpg" />
            <meta name="twitter:card" content="summary_large_image" />
            <meta name="twitter:title" content="Utility Corridor Vegetation Management BC | Boultbee" />
            <meta name="twitter:description" content="Vegetation management for BC utility corridors, substations, and transmission lines. Power line clearance and cable strip maintenance for power, gas, and telecom providers." />
            <meta name="twitter:image" content="https://www.weedmanager.com/images/og-utility.jpg" />
            <script type="application/ld+json">
              {JSON.stringify({
                "@context": "https://schema.org",
                "@type": "BreadcrumbList",
                "itemListElement": [
                  { "@type": "ListItem", "position": 1, "name": "Home", "item": "https://www.weedmanager.com/" },
                  { "@type": "ListItem", "position": 2, "name": "Utility Weed Control", "item": "https://www.weedmanager.com/industries/utility" }
                ]
              })}
            </script>
            <script type="application/ld+json">
              {JSON.stringify({
                "@context": "https://schema.org",
                "@type": "Service",
                "serviceType": "Utility Corridor Vegetation Management",
                "provider": {
                  "@type": "LocalBusiness",
                  "name": "Boultbee Vegetation Management",
                  "url": "https://www.weedmanager.com"
                },
                "areaServed": { "@type": "AdministrativeArea", "name": "British Columbia, Canada" },
                "description": "Herbicide application for utility corridors, substations, transmission lines, and cable strips serving power, gas, and telecommunications providers across BC."
              })}
            </script>
          </Helmet>

          <PageHero
            title="Utility Weed Control"
            subtitle="Herbicide application for power, gas, and telecommunications infrastructure"
            backgroundImage={heroUtility}
            bgPosition="bottom center"
          />

          <AnimateOnScroll animation="fade-in-up">
            <p className="text-center text-lg md:text-xl font-serif italic text-green-900 max-w-3xl mx-auto px-6 py-6">Boultbee maintains vegetation-free conditions for substations, transmission corridors, and cable strips for utility operators province-wide.</p>
          </AnimateOnScroll>

          <AnimateOnScroll animation="fade-in-up">
            <div className="text-container-blur">
              <h2 className="text-xl md:text-2xl font-bold text-gray-900 mb-3">Substation & Switchyard Bare Ground Control</h2>
              <p className="text-gray-700">Vegetation inside substations and switchyards creates fire risk, equipment access issues, and electrical fault hazards. Our bare ground programs maintain total vegetation elimination around transformers, breakers, bus work, control buildings, and perimeter fencing. We apply a mix of pre-emergent and post-emergent herbicides as needed to ensure season-long control.</p>
            </div>
          </AnimateOnScroll>

          <AnimateOnScroll animation="fade-in-up">
            <div className="text-container-blur">
              <h2 className="text-xl md:text-2xl font-bold text-gray-900 mb-3">Transmission & Distribution Corridor Management</h2>
              <p className="text-gray-700">Transmission and distribution rights-of-way require ongoing vegetation management to maintain clearance distances, prevent outages, and ensure access for maintenance crews. Boultbee provides selective herbicide programs that control incompatible vegetation while maintaining low-growing ground cover that prevents erosion. We treat tower foundations, pole bases, guy wire anchors, and access roads within utility corridors throughout BC.</p>
            </div>
          </AnimateOnScroll>

          <AnimateOnScroll animation="fade-in-up">
            <div className="text-container-blur">
              <h2 className="text-xl md:text-2xl font-bold text-gray-900 mb-3">Cable Strip & Telecommunications Infrastructure</h2>
              <p className="text-gray-700">Underground cable strips, fiber optic routes, and telecommunications equipment pads require bare ground maintenance to prevent root damage and maintain access for repairs. Boultbee provides targeted herbicide programs for cable strips, pedestal bases, cabinet pads, and equipment compounds. Our precision application methods control vegetation without affecting adjacent landscaping or public areas.</p>
            </div>
          </AnimateOnScroll>

          {/* Other Industries We Serve */}
          <AnimateOnScroll animation="fade-in-up">
            <div className="text-container-blur">
              <h2 className="text-xl md:text-2xl font-bold text-gray-900 mb-4 text-center">Other Industries We Serve</h2>
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                <Link to="/industries/railroad" className="block bg-white/80 rounded-lg p-4 text-center hover-lift border border-gray-200">
                  <svg className="w-6 h-6 text-green-700 mx-auto mb-2" fill="none" stroke="currentColor" strokeWidth="1.5" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" d="M8 18h8M8 18l-2 3m10-3 2 3M6 6h12v8a4 4 0 0 1-4 4H10a4 4 0 0 1-4-4V6Zm0 0V4h12v2M9 10h6" /></svg>
                  <h3 className="font-bold text-gray-900">Railroads</h3>
                  <p className="text-gray-700 text-sm mt-1">High-rail application & ballast control</p>
                </Link>
                <Link to="/industries/oil-gas" className="block bg-white/80 rounded-lg p-4 text-center hover-lift border border-gray-200">
                  <svg className="w-6 h-6 text-green-700 mx-auto mb-2" fill="none" stroke="currentColor" strokeWidth="1.5" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" d="M12 2C12 2 7 8.5 7 13a5 5 0 0 0 10 0c0-4.5-5-11-5-11Z" /></svg>
                  <h3 className="font-bold text-gray-900">Oil & Gas</h3>
                  <p className="text-gray-700 text-sm mt-1">Refineries, pipelines & tank farms</p>
                </Link>
                <Link to="/industries/municipal" className="block bg-white/80 rounded-lg p-4 text-center hover-lift border border-gray-200">
                  <svg className="w-6 h-6 text-green-700 mx-auto mb-2" fill="none" stroke="currentColor" strokeWidth="1.5" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" d="M8.25 21v-4.875c0-.621.504-1.125 1.125-1.125h5.25c.621 0 1.125.504 1.125 1.125V21m0 0h4.5V8.25l-9-6.75-9 6.75V21h4.5" /></svg>
                  <h3 className="font-bold text-gray-900">Municipalities</h3>
                  <p className="text-gray-700 text-sm mt-1">Parks, sidewalks & public spaces</p>
                </Link>
              </div>
            </div>
          </AnimateOnScroll>

          <AnimateOnScroll animation="fade-in-up">
            <div className="text-container-blur text-center">
              <h2 className="text-xl md:text-2xl font-bold text-gray-900 mb-3">Get a Quote</h2>
              <p className="text-gray-700 mb-5">We work with utilities of all sizes across British Columbia. <Link to="/services" className="text-green-700 underline hover:text-green-900">View all services</Link> or contact us to discuss your infrastructure needs.</p>
              <Link to="/contact" className="cta-button">
                Get a Quote
              </Link>
            </div>
          </AnimateOnScroll>
        </div>
      </div>
    </div>
  );
};

export default UtilityPage;
